/**
 * Market Detail Appendix Slide
 * Compact summary of 5-tier market numbers + data sources + methodology
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { media } from '@config/breakpoints';
import {
  AppendixSlideLayout,
  AppendixContentArea,
  SlideHeader,
  AppendixColumn,
  AppendixSectionLabel,
} from '@components/press/pitch-deck/slide-primitives';

// ============================================
// Local overrides (market-detail uses slightly larger sizes)
// ============================================

const SlideLayout = styled(AppendixSlideLayout)`
  padding: ${spacing.md} ${spacing.lg};
  ${media.md} {
    padding: ${spacing.lg} ${spacing['2xl']};
  }
`;


const ContentArea = styled(AppendixContentArea)`
  gap: ${spacing.lg};
  overflow: hidden;
  ${media.md} {
    gap: ${spacing.xl};
  }
`;

const LeftColumn = styled(AppendixColumn)`
  overflow-y: visible;
`;

const RightColumn = AppendixColumn;

const SectionLabel = AppendixSectionLabel;

// ============================================
// Compact Summary Table
// ============================================

const SummaryTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

interface SummaryRowProps {
  $accent: string;
  $bg: string;
}

const SummaryRow = styled.div<SummaryRowProps>`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.md};
  background: ${(p) => p.$bg};
  border-left: 3px solid ${(p) => p.$accent};
  border-radius: ${borderRadius.sm};
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.xs});
`;

const RowName = styled.div`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
`;

const RowDemo = styled.div`
  color: ${colors.highlight.light};
  font-size: clamp(8px, 0.8vw, ${typography.fontSize.xxs});
`;

const RowCell = styled.div`
  text-align: right;
  min-width: 50px;
  color: ${colors.highlight.dark};
`;

const RowCellBold = styled(RowCell)`
  font-weight: ${typography.fontWeight.bold};
  font-size: clamp(${typography.fontSize.xxs}, 1vw, ${typography.fontSize.xs});
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: ${spacing.xs};
  padding: 0 ${spacing.sm} 2px;
  font-family: ${typography.fontFamily.body};
  font-size: clamp(8px, 0.8vw, ${typography.fontSize.xxs});
  color: ${colors.highlight.light};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.border};
`;

// ============================================
// Data Sources
// ============================================

const SourceList = styled.ol`
  margin: 0;
  padding-left: ${spacing.md};
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  line-height: 1.5;
  list-style-type: decimal;

  li {
    margin-bottom: 1px;
  }
`;

// ============================================
// Methodology
// ============================================

const MethodologyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MethodItem = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  line-height: 1.4;
  padding-left: ${spacing.sm};
  border-left: 2px solid ${colors.brand.primaryDark};
`;

const MethodLabel = styled.span`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
`;

// ============================================
// Data
// ============================================

const TIERS = [
  {
    name: 'Beachhead',
    demo: 'Dual-income \u00b7 Kids \u00b7 CA \u00b7 $250K+',
    hh: '310K',
    arr10: '$6.7M',
    arr50: '$33.4M',
    arr100: '$66.8M',
    accent: colors.accent.coral.exxtraDark,
    bg: colors.accent.coral.exxtraLight + '40',
  },
  {
    name: 'Adjacent',
    demo: 'Dual-income \u00b7 Kids \u00b7 US \u00b7 $250K+',
    hh: '2.2M',
    arr10: '$47M',
    arr50: '$237M',
    arr100: '$474M',
    accent: colors.accent.teal.exxtraDark,
    bg: colors.accent.teal.extraLight + '50',
  },
  {
    name: 'SOM',
    demo: 'Dual-income \u00b7 Kids \u00b7 US \u00b7 $100K+',
    hh: '10.5M',
    arr10: '$226M',
    arr50: '$1.13B',
    arr100: '$2.26B',
    accent: colors.accent.blue.exxtraDark,
    bg: colors.accent.blue.extraLight + '50',
  },
  {
    name: 'SAM',
    demo: 'All families w/ kids \u00b7 US \u00b7 $100K+',
    hh: '16M',
    arr10: '$345M',
    arr50: '$1.72B',
    arr100: '$3.45B',
    accent: colors.accent.purple.exxtraDark,
    bg: colors.accent.purple.extraLight + '50',
  },
  {
    name: 'TAM',
    demo: 'All US households \u00b7 $100K+',
    hh: '54.5M',
    arr10: '$1.17B',
    arr50: '$5.87B',
    arr100: '$11.7B',
    accent: colors.highlight.primary,
    bg: colors.neutral.ultraLight + '80',
  },
];

const DATA_SOURCES = [
  'US Census Bureau, ACS 2024 1-Year Estimates: Total US HH (~133M), CA HH (~13.8M), median HHI.',
  'FRED (St. Louis Fed), Series TTLFMCU: Total US families w/ children under 18 = 33.3K (2024).',
  'US Census Bureau, CPS 2024: Family HH = ~64% of all HH; married-couple families w/ kids: ~23.5M.',
  'World Population Review (ACS 2023): CA HH earning $250K+ \u2248 10%; US HH earning $250K+ \u2248 8.5%.',
  'BLS Consumer Expenditure Survey (2015-17): 66% of married couples w/ kids are dual-income.',
  'Pew Research Center: Median income for dual-earner couples w/ children \u2248 $151,900 (2024$).',
  'DQYDJ / CPS ASEC 2025: $100K \u2248 59th percentile of US HHI \u2192 ~41% of HH earn $100K+.',
  'Census HINC-04 tables: Income distribution by family type and presence of children.',
];

const METHODOLOGY = [
  { label: 'Dual Income', text: 'Both spouses/partners employed (FT+FT or FT+PT). BLS CEX 2015-17.' },
  { label: 'Kids', text: 'Own children under 18 in household (Census definition). FRED TTLFMCU.' },
  { label: '$250K+ Threshold', text: 'Census top-codes at $250K; ~top 8.5% of US HH. Families w/ kids skew ~10-11%.' },
  { label: '$100K+ Threshold', text: '~41% of all US HH. Captures "comfortable but time-starved" families (<0.2% of income).' },
  { label: 'CA vs US', text: 'CA has ~10.4% of US HH but higher wages; ~50% of CA HH earn $100K+ vs ~41% nationally.' },
  { label: 'Beachhead Logic', text: 'Most time-starved, highest WTP, reachable via Bay Area/LA tech networks.' },
  { label: 'Pricing', text: '$17.95/mo = $215.40/yr. Netflix-equivalent for a universal daily pain point.' },
];

// ============================================
// Component
// ============================================

export const MarketDetailSlide: React.FC = () => (
  <SlideLayout>
    <SlideHeader
      title="Market Detail"
      subtitle="5-tier beachhead expansion · $17.95/mo · ARR at 10%, 50%, 100% penetration"
    />

    <ContentArea>
      <LeftColumn>
        <SectionLabel>Market Sizing Summary</SectionLabel>
        <TableHeader>
          <div>Tier</div>
          <div style={{ minWidth: 50, textAlign: 'right' }}>Households</div>
          <div style={{ minWidth: 50, textAlign: 'right' }}>ARR @10%</div>
          <div style={{ minWidth: 50, textAlign: 'right' }}>ARR @100%</div>
        </TableHeader>
        <SummaryTable>
          {TIERS.map((tier) => (
            <SummaryRow key={tier.name} $accent={tier.accent} $bg={tier.bg}>
              <div>
                <RowName>{tier.name}</RowName>
                <RowDemo>{tier.demo}</RowDemo>
              </div>
              <RowCellBold>{tier.hh}</RowCellBold>
              <RowCell>{tier.arr10}</RowCell>
              <RowCellBold>{tier.arr100}</RowCellBold>
            </SummaryRow>
          ))}
        </SummaryTable>

        <SectionLabel style={{ marginTop: spacing.xs }}>Key Segmentation Decisions</SectionLabel>
        <MethodologyList>
          {METHODOLOGY.map((m) => (
            <MethodItem key={m.label}>
              <MethodLabel>{m.label}:</MethodLabel> {m.text}
            </MethodItem>
          ))}
        </MethodologyList>
      </LeftColumn>

      <RightColumn>
        <SectionLabel>Data Sources</SectionLabel>
        <SourceList>
          {DATA_SOURCES.map((source, i) => (
            <li key={i}>{source}</li>
          ))}
        </SourceList>

        <SectionLabel style={{ marginTop: spacing.xs }}>Derivation Notes</SectionLabel>
        <MethodologyList>
          <MethodItem>
            <MethodLabel>Beachhead (310K):</MethodLabel> CA has ~13.8M HH. ~10% earn $250K+ = ~1.38M.
            ~25% have kids under 18. Of married couples w/ kids, 66% are dual-income.
            CA dual-income families: ~19% earn $250K+. 1.65M &times; 19% &asymp; 310K.
          </MethodItem>
          <MethodItem>
            <MethodLabel>Adjacent (2.2M):</MethodLabel> ~133M US HH. ~33.3M families w/ kids.
            ~23.5M married-couple, 66% dual-income (~15.5M). ~14% earn $250K+ nationally.
            15.5M &times; 14% &asymp; 2.2M.
          </MethodItem>
          <MethodItem>
            <MethodLabel>SOM (10.5M):</MethodLabel> Dual-income families w/ kids skew higher:
            median ~$151K (Pew), so ~68% earn $100K+. 15.5M &times; 68% &asymp; 10.5M.
          </MethodItem>
          <MethodItem>
            <MethodLabel>SAM (16M):</MethodLabel> All 33.3M US families w/ kids (TTLFMCU).
            Married-couple (24.2M, FMLWCUMC): ~60% earn $100K+ &rarr; 14.5M.
            Single-mother (7.2M): ~11% &rarr; 0.8M. Single-father (1.9M): ~20% &rarr; 0.4M.
            Total: ~15.7M, rounded to 16M. Cross-check: 28% of 54.5M = 15.3M.
          </MethodItem>
          <MethodItem>
            <MethodLabel>TAM (54.5M):</MethodLabel> All US HH earning $100K+. ~41% of 133M total
            US HH (CPS ASEC 2024, $100K &asymp; 59th percentile). 133M &times; 41% &asymp; 54.5M.
          </MethodItem>
        </MethodologyList>
      </RightColumn>
    </ContentArea>
  </SlideLayout>
);
